import { IconSync } from '../ui/icons';
import React from 'react';
import { reloadIfSiteUpdated } from '../../utils/siteVersion';
import { refreshFailedChunk } from '../../utils/lazyWithRetry';

/**
 * ChunkErrorBoundary - Gestion des erreurs de chargement des chunks
 * Résout les erreurs "Loading chunk failed" après déploiement ou mise à jour
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static isChunkError(error) {
    return error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk') ||
      error?.message?.includes('ChunkLoadError');
  }

  static getDerivedStateFromError(error) {
    // Détecte spécifiquement les erreurs de chunk
    const isChunkError = ChunkErrorBoundary.isChunkError(error);

    return {
      hasError: isChunkError,
      error: isChunkError ? error : null
    };
  }

  componentDidCatch(error, errorInfo) {
    // Les autres erreurs sont gérées (et journalisées) par ErrorBoundary
    if (ChunkErrorBoundary.isChunkError(error)) {
      console.warn('Fichier de page introuvable :', error?.message);
      // Site mis à jour pendant la visite : rechargement automatique, sans afficher l'écran d'erreur
      this.setState({ checkingUpdate: true });
      reloadIfSiteUpdated().then(reloading => { if (!reloading) this.setState({ checkingUpdate: false }); });
    }
  }

  handleReload = async () => {
    // Le fichier en échec est d'abord téléchargé de nouveau (cache du navigateur remplacé)
    await refreshFailedChunk(this.state.error);
    // Nettoyer le cache et service workers avant reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => reg.unregister());
      });
    }

    // Force un hard reload
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError && this.state.checkingUpdate) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-live="polite">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" aria-hidden="true" />
          <span className="ml-3 font-medium text-gray-600">Mise à jour du site…</span>
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center border border-gray-100">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-700 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Page non chargée
            </h2>

            <p className="text-gray-600 mb-6 leading-relaxed">
              La connexion internet semble interrompue. Vérifiez votre connexion,
              puis rechargez la page pour continuer.
            </p>

            <button
              onClick={this.handleReload}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-md"
            >
              <IconSync className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> Recharger la page
            </button>

            <p className="text-xs text-gray-500 mt-4">
              Cela arrive après une coupure de connexion (4G, Wi-Fi, mise en veille)
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
